import { Box, Grid } from '@mui/material';
import { forwardRef, PropsWithChildren } from 'react';
import { LoadingCards } from './LoadingCards';
import { CardPaddingTopBottom } from './useVirtualBoardParams';
import { IUseBoardParamsProps, useBoardParams } from './useBoardParams';

export function Board<T extends object>(
  props: IUseBoardParamsProps<T> & {
    isFetching: boolean;
    renderItem: (props: { item: T; index: number }) => JSX.Element;
    EmptyComponent: React.ReactElement;
  },
) {
  const { itemHeight, isLoading, isFetching, renderItem, EmptyComponent } = props;

  const { items, headerHeight, ref, onScroll, isLoaderItem, renderLoaderItem } =
    useBoardParams(props);

  if (isLoading) {
    // 16 is padding top and bottom
    return (
      <GridContainer ref={ref} isLoading={isLoading} headerHeight={headerHeight}>
        <LoadingCards height={itemHeight - CardPaddingTopBottom} />
      </GridContainer>
    );
  }

  if (!isFetching && !items.length) {
    return (
      <GridContainer ref={ref} isLoading={isLoading} headerHeight={headerHeight}>
        {EmptyComponent}
      </GridContainer>
    );
  }

  return (
    <GridContainer ref={ref} isLoading={isLoading} headerHeight={headerHeight} onScroll={onScroll}>
      <Grid container spacing={2} rowSpacing={2}>
        {items.map((item, index) => {
          return isLoaderItem(item) ? renderLoaderItem({ index }) : renderItem({ item, index });
        })}
      </Grid>
    </GridContainer>
  );
}

export const GridContainer = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{
    isLoading: boolean;
    headerHeight: number;
    onScroll?: (e: any) => void;
  }>
>((props, ref: React.ForwardedRef<HTMLDivElement>) => {
  const { isLoading, headerHeight, children, onScroll } = props;
  return (
    <Box
      sx={{
        height: isLoading ? 'auto' : `calc(100vh - ${headerHeight}px)`,
        width: '100%',
        overflowY: 'scroll',
      }}
      ref={ref}
      onScroll={onScroll}
    >
      {children}
    </Box>
  );
});
